import React, { useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { Callback, PagedResponse } from "./types";
import {
  isoDay, daysAgo, fmtInt, fmtDate, short, usePaged, useDebounced,
  Header, DateRange, ErrorBox, Panel, Pager, FilterBar, EmptyRow,
  STATUS_COLORS, inputCls, thCls, tdCls, theadRow, tbodyRow, pageCls,
} from "./shared";

const pickCallbacks = (d: PagedResponse) => d.callbacks;

// Every wallet callback the provider sent us — the first place to look when
// a game shows no balance or a bet fails.
export default function Callbacks() {
  const [verdict, setVerdict] = useState("");
  const [path, setPath] = useState("");
  const [player, setPlayer] = useState("");
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(isoDay(new Date()));
  const dPath = useDebounced(path);
  const dPlayer = useDebounced(player);

  const { rows, pagination, setPage, loading, error, load } = usePaged<Callback>("/callbacks", pickCallbacks, {
    verdict, path: dPath.trim(), player_id: dPlayer.trim(), from, to,
  });

  return (
    <div className={pageCls}>
      <Header title="Original Games — Callback Log" subtitle="Every wallet callback the provider sent, and whether we accepted it" onRefresh={load} loading={loading} />
      <ErrorBox error={error} />
      <Panel title={<>Callbacks <span className="text-sm text-[#878AA2] font-normal ml-2">({fmtInt(pagination.totalCount)} total)</span></>}>
        <FilterBar>
          <select value={verdict} onChange={(e) => setVerdict(e.target.value)} className={inputCls}>
            <option value="">All verdicts</option>
            <option value="accepted">accepted</option>
            <option value="rejected">rejected</option>
          </select>
          <input value={path} onChange={(e) => setPath(e.target.value)} placeholder="Path (balance, bet…)" className={inputCls + " w-44"} />
          <input value={player} onChange={(e) => setPlayer(e.target.value)} placeholder="Player ID" className={inputCls + " w-40"} />
          <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
        </FilterBar>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className={theadRow}>
              <th className={thCls}>Time</th><th className={thCls}>Path</th><th className={thCls}>Verdict</th><th className={thCls}>Reason</th>
              <th className={thCls}>Player</th><th className={thCls}>Transaction</th><th className={thCls}>Secret</th><th className={thCls}>Source IP</th>
            </tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className={tbodyRow}>
                  <td className={tdCls + " text-[#878AA2]"}>{fmtDate(c.created_at)}</td>
                  <td className={tdCls + " font-mono text-xs"}>{c.path.replace("/originalgames", "")}</td>
                  <td className={tdCls}>
                    <span className={`flex items-center gap-1 ${STATUS_COLORS[c.verdict] || ""}`}>
                      {c.verdict === "accepted" ? <CheckCircle2 size={14} /> : <XCircle size={14} />}{c.verdict}
                    </span>
                  </td>
                  <td className={tdCls + " text-[#878AA2] max-w-xs truncate"} title={c.reason || ""}>{c.reason || "—"}</td>
                  <td className={tdCls + " font-mono text-xs"}>{c.player_id || "—"}</td>
                  <td className={tdCls + " font-mono text-xs text-[#878AA2]"} title={c.transaction_id || ""}>{short(c.transaction_id, 13)}</td>
                  <td className={tdCls + " text-[#878AA2]"}>{c.matched_secret || "—"}</td>
                  <td className={tdCls + " font-mono text-xs text-[#878AA2]"}>{c.source_ip || "—"}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && <EmptyRow cols={8} text="No callbacks in this range" />}
            </tbody>
          </table>
        </div>
        <Pager p={pagination} onPage={setPage} />
      </Panel>
    </div>
  );
}
